/**
 * Каталог встроенных критериев (#868, спека `docs/plans/2026-10-01-builtin-criteria.md`
 * §4, план Э1 `docs/plans/2026-10-02-builtin-criteria-e1.md`).
 *
 * Один источник для засева и сверки (PR 5 плана), конструктора и тестов.
 * Формулировка встроенного фиксирована: клиент её не правит
 * (`BUILTIN_FIXED_FIELDS` в маршруте критериев), меняется она только здесь —
 * и каждая правка становится новой версией у всех организаций при следующей
 * сверке.
 *
 * Украинская редакция — эталон: на ней стоят числа замера Э0
 * (`docs/eval/2026-10-02-builtin-criteria-measurement.md` §4), у
 * `callback_offered` — замера редакции 2 (#2090,
 * `docs/eval/2026-10-12-callback-criterion-v2.md`): вопрос о договорённости
 * сторон, шаг, который назвал клиент, а менеджер принял, засчитывается
 * (решение владельца 12.10). ru / en / es —
 * переводы смысла, а не новые формулировки; исключения («не считается»)
 * переносятся все — на них держится точность. `tcpa_compliant` замера не
 * имеет: на проде нет организаций с юрисдикцией US.
 *
 * В en / es сторона компании — «employee» / «el empleado», как на экранах
 * (#1451); в uk / ru — «менеджер», как в замеренной редакции.
 */
import type { Language } from '../i18n/types';
import type { CallCriterionDirection, CallCriterionPauseReason } from './stored';

export const BUILTIN_CRITERION_KEYS = [
  'company_named',
  'callback_offered',
  'recording_disclosed',
  'satisfaction_guarantee',
  'agent_profanity',
  'forbidden_words',
  'tcpa_compliant',
  'data_privacy_violation',
] as const;
export type BuiltinCriterionKey = (typeof BUILTIN_CRITERION_KEYS)[number];

/**
 * С чего начинается встроенная строка НОВОЙ организации (засев при первой
 * сверке, новый ключ каталога). Тумблеров профиля, которые раньше задавали
 * область и паузу, больше нет (Э3, #2846, спека `docs/plans/2026-10-27-builtin-criteria-e3.md` §5),
 * поэтому стартовые значения — статическая таблица. Числа сняты 27.10 с
 * `createDefaultBoundProfiles` до удаления тумблеров: обязательные фразы
 * включены в категориях product_inquiry, purchase, scheduling, follow_up;
 * уведомление о записи не включено нигде; мат проверяется везде.
 *
 * Ключа нет в таблице — его область и пауза идут по каталогу, юрисдикции и
 * списку слов (`tcpa_compliant`, `data_privacy_violation`, `forbidden_words`).
 * Существующую строку таблица не трогает: область и паузу строки ведёт клиент.
 */
export interface BuiltinDefaultSeed {
  /** Категории звонка, в которых критерий действует; `null` — везде. */
  categories: readonly string[] | null;
  /** Строка засевается на паузе с этой причиной; `null` — активной. */
  pauseReason: CallCriterionPauseReason | null;
}

const PHRASE_SEED_CATEGORIES = [
  'product_inquiry',
  'purchase',
  'scheduling',
  'follow_up',
] as const;

export const BUILTIN_DEFAULT_SEED: Readonly<
  Partial<Record<BuiltinCriterionKey, BuiltinDefaultSeed>>
> = {
  company_named: { categories: PHRASE_SEED_CATEGORIES, pauseReason: null },
  callback_offered: { categories: PHRASE_SEED_CATEGORIES, pauseReason: null },
  satisfaction_guarantee: { categories: PHRASE_SEED_CATEGORIES, pauseReason: null },
  agent_profanity: { categories: null, pauseReason: null },
  // Раньше тумблер записи был выключен во всех стандартных профилях: строка
  // стартует на паузе, которую клиент снимает сам (не `profile` — профиля нет).
  recording_disclosed: { categories: null, pauseReason: 'customer' },
};

/**
 * Когда встроенный критерий вообще применим к организации — решает сверка
 * (PR 5), а не шаг оценки:
 * - `profile` — по умолчаниям засева `BUILTIN_DEFAULT_SEED` (до Э3 — по тумблерам профиля, §7 спеки);
 * - `jurisdiction_us` — только организация с юрисдикцией US;
 * - `jurisdiction_concrete` — только при конкретной юрисдикции (подставляется
 *   в текст, `{jurisdiction}`);
 * - `word_list` — у всех, но оценивать нечего, пока клиент не задал список
 *   (#2784): пустой список — пауза `no_words`.
 * Пауза по решению Aelo (`pause_reason = 'catalog'`) — отдельный флаг
 * `catalogPaused`, а не вид применимости: снимется пауза — условие
 * применимости останется. Сейчас на такой паузе нет ни одного ключа: с
 * `data_privacy_violation` (В9) она снята 03.11 с ред. 4 формулировки (#2853,
 * `docs/eval/2026-11-03-data-privacy-wording-measurement.md`), с
 * `satisfaction_guarantee` (В2) — 03.11 с ред. 4 (#3137,
 * `docs/eval/2026-11-03-satisfaction-guarantee-scope-measurement.md`); оба —
 * по слову владельца.
 */
export type BuiltinApplicability =
  'profile' | 'jurisdiction_us' | 'jurisdiction_concrete' | 'word_list';

export interface BuiltinCriterionTexts {
  name: string;
  question: string;
  guidance: string;
}

export interface BuiltinCriterionEntry {
  key: BuiltinCriterionKey;
  resultType: 'flag';
  /** `absence` — срабатывает, если не прозвучало; `presence` — если прозвучало. */
  looksFor: 'presence' | 'absence';
  /** `null` — звонки и чаты. */
  channels: readonly ('call' | 'chat')[] | null;
  /** `null` — без ограничения по направлению. */
  directions: readonly CallCriterionDirection[] | null;
  /**
   * Категории продаж (`PURCHASE_INTENT_CATEGORIES` в `@aica/api-types`) —
   * значение подставляет сверка: core не берёт значения из api-types.
   */
  purchaseIntentOnly: boolean;
  applicability: BuiltinApplicability;
  catalogPaused: boolean;
  /**
   * Чья реплика может доказать ответ. Задано — доказательство от другой
   * стороны или неизвестного говорящего отбрасывается при заземлении, и
   * «прозвучало» без своей реплики не засчитывается (`criterionSignalOutcome`).
   * Не задано — любая реплика, как у своих критериев.
   */
  evidenceSpeaker?: 'agent';
  texts: Record<Language, BuiltinCriterionTexts>;
}

/** Место подстановки юрисдикции в тексте. Другого параметра у формулировок нет. */
export const BUILTIN_JURISDICTION_PLACEHOLDER = '{jurisdiction}';

export const BUILTIN_CRITERIA_CATALOG: readonly BuiltinCriterionEntry[] = [
  {
    key: 'company_named',
    resultType: 'flag',
    looksFor: 'absence',
    channels: null,
    directions: null,
    purchaseIntentOnly: false,
    applicability: 'profile',
    catalogPaused: false,
    texts: {
      uk: {
        name: 'Назвав компанію',
        question: 'Чи назвав менеджер компанію, від імені якої він говорить?',
        guidance:
          'Рахується: менеджер називає СВОЮ компанію чи магазин — зазвичай у привітанні («Магазин X, мене звати…», «Я з компанії X», «Компанія X, слухаю») — або прямо підтверджує її назву. Не рахується: назви виробників, брендів, моделей; назви ІНШИХ компаній — постачальників, конкурентів, дилерів, партнерів, лізингових компаній, банків, служб доставки; назву вимовив лише клієнт; автовідповідач до з’єднання з менеджером.',
      },
      ru: {
        name: 'Назвал компанию',
        question: 'Назвал ли менеджер компанию, от имени которой он говорит?',
        guidance:
          'Считается: менеджер называет СВОЮ компанию или магазин — обычно в приветствии («Магазин X, меня зовут…», «Я из компании X», «Компания X, слушаю») — или прямо подтверждает её название. Не считается: названия производителей, брендов, моделей; названия ДРУГИХ компаний — поставщиков, конкурентов, дилеров, партнёров, лизинговых компаний, банков, служб доставки; название произнёс только клиент; автоответчик до соединения с менеджером.',
      },
      en: {
        name: 'Named the company',
        question: 'Did the employee name the company they are speaking on behalf of?',
        guidance:
          'Counts: the employee names THEIR OWN company or store — usually in the greeting ("Store X, my name is…", "I’m calling from company X", "Company X, how can I help") — or explicitly confirms its name. Does not count: names of manufacturers, brands, models; names of OTHER companies — suppliers, competitors, dealers, partners, leasing companies, banks, delivery services; the name was said only by the customer; an auto-attendant before the call reaches the employee.',
      },
      es: {
        name: 'Nombró la empresa',
        question: '¿El empleado nombró la empresa en nombre de la cual habla?',
        guidance:
          'Cuenta: el empleado nombra SU PROPIA empresa o tienda — normalmente en el saludo («Tienda X, mi nombre es…», «Le hablo de la empresa X», «Empresa X, dígame») — o confirma su nombre de forma explícita. No cuenta: nombres de fabricantes, marcas, modelos; nombres de OTRAS empresas — proveedores, competidores, distribuidores, socios, arrendadoras, bancos, servicios de entrega; el nombre lo dijo solo el cliente; la contestadora automática antes de que la llamada llegue al empleado.',
      },
    },
  },
  {
    key: 'callback_offered',
    resultType: 'flag',
    looksFor: 'absence',
    channels: null,
    directions: null,
    purchaseIntentOnly: false,
    applicability: 'profile',
    catalogPaused: false,
    texts: {
      uk: {
        name: 'Домовилися про наступний контакт',
        question:
          'Чи домовилися менеджер і клієнт про конкретний наступний контакт — дзвінок, повідомлення, надсилання матеріалів чи передачу контакту, — хоч би хто з них цей крок запропонував?',
        guidance:
          'Рахується будь-який конкретний наступний контакт, про який домовилися сторони, навіть без точного часу і хоч би хто його назвав: менеджер пообіцяв («передзвоню», «уточню й наберу», «напишу у вайбер», «надішлю прайс / шаблон», «з вами зв’яжеться X») чи дав номер колеги й попросив набрати; клієнт попросив набрати пізніше, сказав, що сам набере чи подумає й повідомить, і менеджер погодився («я подумаю й дам знати», «я вас наберу», «скажу завтра зранку» — «добре», «чекаю», «давай так») чи сам про це попросив («наберете мене, як вирішите»). Не рахується: лише «звертайтеся», «якщо що — телефонуйте», «будемо на зв’язку» без конкретного кроку; ввічливе прощання; «подумаю» без обіцянки повідомити.',
      },
      ru: {
        name: 'Договорились о следующем контакте',
        question:
          'Договорились ли менеджер и клиент о конкретном следующем контакте — звонке, сообщении, отправке материалов или передаче контакта, — кто бы из них этот шаг ни предложил?',
        guidance:
          'Считается любой конкретный следующий контакт, о котором договорились стороны, даже без точного времени и кто бы его ни назвал: менеджер пообещал («перезвоню», «уточню и наберу», «напишу в вайбер», «отправлю прайс / шаблон», «с вами свяжется X») или дал номер коллеги и попросил набрать; клиент попросил набрать позже, сказал, что сам наберёт или подумает и сообщит, и менеджер согласился («я подумаю и дам знать», «я вас наберу», «скажу завтра утром» — «хорошо», «жду», «давайте так») или сам об этом попросил («наберёте меня, как решите»). Не считается: только «обращайтесь», «если что — звоните», «будем на связи» без конкретного шага; вежливое прощание; «подумаю» без обещания сообщить.',
      },
      en: {
        name: 'Agreed on the next contact',
        question:
          'Did the employee and the customer agree on a concrete next contact — a call, a message, sending materials or passing on a contact — no matter which of them proposed it?',
        guidance:
          'Counts: any concrete next contact the two sides agreed on, even with no exact time, whoever named it: the employee promised it ("I’ll call you back", "I’ll check and call you", "I’ll message you on WhatsApp", "I’ll send the price list / template", "X will contact you") or gave a colleague’s number to call; the customer asked to be called later or said they would call or think it over and get back, and the employee agreed ("I’ll think it over and let you know", "I’ll call you", "I’ll tell you tomorrow morning" — "sure", "I’ll wait", "let’s do that") or asked for it ("call me once you decide"). Does not count: only "feel free to reach out", "call if anything comes up", "we’ll stay in touch" with no concrete step; a polite goodbye; "I’ll think about it" with no promise to get back.',
      },
      es: {
        name: 'Acordaron el próximo contacto',
        question:
          '¿El empleado y el cliente acordaron un próximo contacto concreto — una llamada, un mensaje, el envío de materiales o pasar un contacto —, sin importar cuál de los dos lo propuso?',
        guidance:
          'Cuenta: cualquier próximo contacto concreto que acordaron las partes, aunque sin hora exacta y sin importar quién lo propuso: el empleado lo prometió («le devuelvo la llamada», «lo reviso y le marco», «le escribo por WhatsApp», «le envío la lista de precios», «X se comunicará con usted») o le pasó el número de un colega y le pidió marcarle; el cliente pidió que le marcaran más tarde o dijo que marcaría o lo pensaría y avisaría, y el empleado aceptó («lo pienso y le aviso», «yo le marco», «le digo mañana temprano» — «perfecto», «quedo pendiente», «así quedamos») o se lo pidió él mismo («márqueme cuando decida»). No cuenta: solo «estamos a sus órdenes», «cualquier cosa, me marca», «seguimos en contacto» sin un paso concreto; una despedida cortés; «lo voy a pensar» sin prometer avisar.',
      },
    },
  },
  {
    key: 'recording_disclosed',
    resultType: 'flag',
    looksFor: 'absence',
    channels: ['call'],
    directions: null,
    purchaseIntentOnly: false,
    applicability: 'profile',
    catalogPaused: false,
    texts: {
      uk: {
        name: 'Попередив про запис',
        question:
          'Чи повідомив менеджер або система, що розмова записується чи може прослуховуватися?',
        guidance:
          'Рахується: фраза на кшталт «розмова записується», «дзвінок може бути записаний з метою контролю якості» — від менеджера або автоматичного повідомлення на початку дзвінка. Не рахується: згадка про запис інших розмов, запис голосового повідомлення, «запишіть номер», запис на прийом чи до лікаря.',
      },
      ru: {
        name: 'Предупредил о записи',
        question:
          'Сообщил ли менеджер или система, что разговор записывается или может прослушиваться?',
        guidance:
          'Считается: фраза вроде «разговор записывается», «звонок может быть записан в целях контроля качества» — от менеджера или автоматического сообщения в начале звонка. Не считается: упоминание записи других разговоров, запись голосового сообщения, «запишите номер», запись на приём или к врачу.',
      },
      en: {
        name: 'Disclosed call recording',
        question:
          'Did the employee or the system say that the conversation is being recorded or may be monitored?',
        guidance:
          'Counts: a phrase such as "this call is being recorded" or "this call may be recorded for quality purposes" — from the employee or an automated message at the start of the call. Does not count: mentions of recording other conversations, recording a voice message, "write down the number", booking an appointment.',
      },
      es: {
        name: 'Avisó de la grabación',
        question:
          '¿El empleado o el sistema informó que la conversación se está grabando o puede ser monitoreada?',
        guidance:
          'Cuenta: una frase como «esta llamada está siendo grabada» o «esta llamada puede ser grabada con fines de calidad» — del empleado o de un mensaje automático al inicio de la llamada. No cuenta: menciones de grabar otras conversaciones, grabar un mensaje de voz, «anote el número», agendar una cita.',
      },
    },
  },
  {
    // Ред. 5 (#3143): когда гарантию уместно требовать, решает пояснение —
    // на остальных звонках модель отвечает «не применимо», а не «не прозвучало».
    // Пауза В2 снята 03.11 по слову владельца при точности 44–59 % на Stimma.
    // Короткое подтверждение на вопрос клиента о возврате или обмене считается
    // объяснением — слово владельца 04.11: разметчики расходились именно здесь,
    // а модель требовала срок или условие. Замер —
    // `docs/eval/2026-11-04-absence-client-trigger-measurement.md`.
    // Не `purchaseIntentOnly`: предел каталога по категориям сделал бы правку
    // области у существующих строк (с `follow_up`) отказом 409. Замер —
    // `docs/eval/2026-11-03-satisfaction-guarantee-scope-measurement.md`.
    key: 'satisfaction_guarantee',
    resultType: 'flag',
    looksFor: 'absence',
    channels: null,
    directions: null,
    purchaseIntentOnly: false,
    applicability: 'profile',
    catalogPaused: false,
    texts: {
      uk: {
        name: 'Пояснив гарантію задоволеності',
        question:
          'Чи пояснив менеджер клієнту гарантію задоволеності — повернення коштів або обмін, якщо товар чи послуга не влаштують?',
        guidance:
          'Застосовно, лише якщо клієнт сам прямо питає, чи можна повернути або обміняти, або каже, що боїться, що товар чи послуга не підійде чи не сподобається («а якщо не підійде за розміром?»). Без такої репліки клієнта — не застосовно: питання про характеристики, сумісність, розмір, ціну, наявність чи вибір моделі; відмова чи відкладання покупки з інших причин (ціна, гроші, «подумаю»); сервіс, доставка, статус чи підтвердження замовлення, оплата. Рахується: менеджер говорить про можливість повернути гроші, обміняти товар або скасувати послугу, якщо клієнту не сподобається, хоч би коротким «так» на питання клієнта про повернення чи обмін, без строку й умов. Не рахується: гарантія виробника на ремонт; клієнт питає про повернення, а менеджер відповідає лише про брак; умови доставки.',
      },
      ru: {
        name: 'Объяснил гарантию удовлетворённости',
        question:
          'Объяснил ли менеджер клиенту гарантию удовлетворённости — возврат денег или обмен, если товар или услуга не подойдут?',
        guidance:
          'Применимо, только если клиент сам прямо спрашивает, можно ли вернуть или обменять, или говорит, что боится, что товар или услуга не подойдёт или не понравится («а если не подойдёт по размеру?»). Без такой реплики клиента — не применимо: вопросы о характеристиках, совместимости, размере, цене, наличии или выборе модели; отказ или отсрочка покупки по другим причинам (цена, деньги, «подумаю»); сервис, доставка, статус или подтверждение заказа, оплата. Считается: менеджер говорит о возможности вернуть деньги, обменять товар или отменить услугу, если клиенту не понравится, хотя бы коротким «да» на вопрос клиента о возврате или обмене, без срока и условий. Не считается: гарантия производителя на ремонт; клиент спрашивает о возврате, а менеджер отвечает только про брак; условия доставки.',
      },
      en: {
        name: 'Explained the satisfaction guarantee',
        question:
          'Did the employee explain a satisfaction guarantee to the customer — a refund or exchange if the product or service does not suit them?',
        guidance:
          'Applies only if the customer directly asks whether they can return or exchange, or says they fear the product or service will not suit or please them (“what if the size is wrong?”). Otherwise it does not apply: questions about specs, compatibility, size, price, stock or choosing a model; refusing or putting off buying for other reasons (price, money, “I’ll think about it”); service, delivery, order status or confirmation, payment. Counts: the employee states the option to get a refund, exchange the product or cancel the service if the customer is not satisfied, even a short yes to their question about a return or exchange, no time limit or condition. Does not count: a manufacturer’s repair warranty; the customer asks about returns and the employee only talks about defects; delivery terms.',
      },
      es: {
        name: 'Explicó la garantía de satisfacción',
        question:
          '¿El empleado le explicó al cliente una garantía de satisfacción — reembolso o cambio si el producto o servicio no le convence?',
        guidance:
          'Se aplica solo si el cliente pregunta directamente si puede devolver o cambiar, o dice que teme que el producto o servicio no le sirva o no le guste («¿y si la talla no me queda?»). Si no, no se aplica: dudas sobre características, compatibilidad, talla, precio, stock o elección de modelo; rechazar o aplazar la compra por otros motivos (precio, dinero, «lo pensaré»); servicio, entrega, estado o confirmación del pedido, pago. Cuenta: el empleado menciona la opción de devolver el dinero, cambiar el producto o cancelar el servicio si al cliente no le gusta, aunque sea un breve sí a su pregunta sobre devolución o cambio, sin plazo ni condición. No cuenta: garantía de reparación del fabricante; el cliente pregunta por devoluciones y el empleado solo habla de defectos; condiciones de entrega.',
      },
    },
  },
  {
    key: 'agent_profanity',
    resultType: 'flag',
    looksFor: 'presence',
    channels: null,
    directions: null,
    purchaseIntentOnly: false,
    applicability: 'profile',
    catalogPaused: false,
    // Формулировка исключает брань клиента и реплики неизвестного автора, но
    // держала это только текстом: ссылку модели на такую реплику заземление
    // принимало (#2821). Замер на проде 31.10: из 208 «прозвучало» ни одного
    // со ссылкой на клиента, 6 — только на неизвестного говорящего (моно-запись).
    evidenceSpeaker: 'agent',
    texts: {
      uk: {
        name: 'Нецензурна лайка менеджера',
        question: 'Чи вживав менеджер нецензурну лайку або образливі слова?',
        guidance:
          'Рахуються лише репліки менеджера: нецензурні слова (мат будь-якою мовою, в будь-якій формі) або образи на адресу клієнта чи третіх осіб. Не рахується: лайка клієнта; репліки, автор яких невідомий; м’які вигуки й евфемізми — блін, бляха, чорт, капець, йолки, «говно вопрос» у значенні «без проблем», «до біса».',
      },
      ru: {
        name: 'Нецензурная брань менеджера',
        question: 'Употреблял ли менеджер нецензурную брань или оскорбительные слова?',
        guidance:
          'Считаются только реплики менеджера: нецензурные слова (мат на любом языке, в любой форме) или оскорбления в адрес клиента или третьих лиц. Не считается: брань клиента; реплики, автор которых неизвестен; мягкие восклицания и эвфемизмы — блин, бляха-муха, чёрт, капец, ёлки, «говно вопрос» в значении «без проблем», «к чёрту».',
      },
      en: {
        name: 'Employee used profanity',
        question: 'Did the employee use profanity or insulting language?',
        guidance:
          'Only the employee’s lines count: obscene words (swearing in any language and any form) or insults aimed at the customer or third parties. Does not count: the customer’s swearing; lines whose speaker is unknown; mild exclamations and euphemisms — damn, darn, heck, crap, "what the hell".',
      },
      es: {
        name: 'El empleado usó groserías',
        question: '¿El empleado usó groserías o palabras ofensivas?',
        guidance:
          'Solo cuentan las intervenciones del empleado: palabras obscenas (groserías en cualquier idioma y forma) o insultos dirigidos al cliente o a terceros. No cuenta: las groserías del cliente; intervenciones cuyo autor se desconoce; exclamaciones suaves y eufemismos — caramba, rayos, híjole, chin, «a la porra», «al diablo».',
      },
    },
  },
  {
    // #2784, спека `docs/plans/2026-10-26-forbidden-words-builtin-criterion.md`:
    // формулировка наша, список — клиента (`word_list` строки, до 100 строк по
    // 64 символа). Модель видит список отдельной строкой определения
    // (`renderCriterion` в prompt-builder), а не в подсказке: 6 400 символов
    // в подсказку на 800 не входят. Только менеджер — решение владельца 26.10.
    key: 'forbidden_words',
    resultType: 'flag',
    looksFor: 'presence',
    channels: null,
    directions: null,
    purchaseIntentOnly: false,
    applicability: 'word_list',
    catalogPaused: false,
    // Формулировка исключает реплики клиента и неизвестного говорящего, но
    // модель могла бы сослаться на них — правило держит заземление, а не
    // только текст (находка Codex на #2815).
    evidenceSpeaker: 'agent',
    texts: {
      uk: {
        name: 'Менеджер вжив заборонене слово',
        question: 'Чи вжив менеджер слово або вираз зі списку заборонених?',
        guidance:
          'Список — у полі words цього критерію. Рахується будь-яка форма слова зі списку: відмінок, число, рід, зменшувальна форма («дорого» — це й «дорожче», «дорогувато»). Рахуються лише репліки менеджера. Не рахується: слово клієнта; репліки, автор яких невідомий; інше слово, що лише починається так само, але має інший зміст.',
      },
      ru: {
        name: 'Менеджер использовал запрещённое слово',
        question: 'Использовал ли менеджер слово или выражение из списка запрещённых?',
        guidance:
          'Список — в поле words этого критерия. Считается любая форма слова из списка: падеж, число, род, уменьшительная форма («дорого» — это и «дороже», «дороговато»). Считаются только реплики менеджера. Не считается: слово клиента; реплики, автор которых неизвестен; другое слово, которое лишь начинается так же, но имеет другой смысл.',
      },
      en: {
        name: 'Employee used a forbidden word',
        question: 'Did the employee use a word or phrase from the forbidden list?',
        guidance:
          'The list is in the words field of this criterion. Any form of a listed word counts: tense, number, a derived or inflected form ("cheap" also covers "cheaper", "cheapest"). Only the employee’s lines count. Does not count: the customer’s words; lines whose speaker is unknown; a different word that merely starts the same way but means something else.',
      },
      es: {
        name: 'El empleado usó una palabra prohibida',
        question: '¿El empleado usó una palabra o expresión de la lista de prohibidas?',
        guidance:
          'La lista está en el campo words de este criterio. Cuenta cualquier forma de una palabra de la lista: género, número, tiempo, diminutivo («barato» también es «baratito», «más barato»). Solo cuentan las intervenciones del empleado. No cuenta: las palabras del cliente; intervenciones cuyo autor se desconoce; otra palabra que solo empieza igual pero significa otra cosa.',
      },
    },
  },
  {
    key: 'tcpa_compliant',
    resultType: 'flag',
    looksFor: 'absence',
    channels: ['call'],
    directions: ['outbound'],
    purchaseIntentOnly: true,
    applicability: 'jurisdiction_us',
    catalogPaused: false,
    texts: {
      uk: {
        name: 'Дотримано вимог TCPA',
        question:
          'Чи дотримався менеджер вимог TCPA під час вихідного дзвінка — назвався, назвав компанію й мету дзвінка та виконав прохання клієнта більше не дзвонити?',
        guidance:
          'Рахується: на початку дзвінка менеджер називає себе й компанію, від імені якої дзвонить, і мету дзвінка; якщо клієнт просить більше не дзвонити чи прибрати номер — менеджер погоджується й не продовжує пропозицію. Не рахується: менеджер не назвався або не назвав компанію; продовжує продаж після прохання не дзвонити чи після відмови; заперечує, що дзвінок рекламний, коли пропонує товар.',
      },
      ru: {
        name: 'Соблюдены требования TCPA',
        question:
          'Соблюдал ли менеджер требования TCPA во время исходящего звонка — представился, назвал компанию и цель звонка и выполнил просьбу клиента больше не звонить?',
        guidance:
          'Считается: в начале звонка менеджер называет себя и компанию, от имени которой звонит, и цель звонка; если клиент просит больше не звонить или убрать номер — менеджер соглашается и не продолжает предложение. Не считается: менеджер не представился или не назвал компанию; продолжает продажу после просьбы не звонить или после отказа; отрицает, что звонок рекламный, когда предлагает товар.',
      },
      en: {
        name: 'Met TCPA requirements',
        question:
          'Did the employee meet TCPA requirements on this outbound call — identify themselves, the company and the purpose of the call, and honor a request not to be called again?',
        guidance:
          'Counts: at the start of the call the employee gives their name, the company they are calling for and the purpose of the call; if the customer asks not to be called again or to be removed from the list, the employee agrees and stops the offer. Does not count: the employee did not give their name or the company; keeps selling after a do-not-call request or a refusal; denies the call is a sales call while pitching a product.',
      },
      es: {
        name: 'Cumplió los requisitos de la TCPA',
        question:
          '¿El empleado cumplió los requisitos de la TCPA en esta llamada saliente — se identificó, nombró la empresa y el motivo de la llamada y respetó la solicitud de no volver a llamar?',
        guidance:
          'Cuenta: al inicio de la llamada el empleado dice su nombre, la empresa en cuyo nombre llama y el motivo de la llamada; si el cliente pide que no lo vuelvan a llamar o que borren su número, el empleado acepta y deja de ofrecer. No cuenta: el empleado no dio su nombre o el de la empresa; sigue vendiendo después de la solicitud de no llamar o de una negativa; niega que sea una llamada de ventas mientras ofrece un producto.',
      },
    },
  },
  {
    key: 'data_privacy_violation',
    resultType: 'flag',
    looksFor: 'presence',
    channels: ['call'],
    directions: null,
    purchaseIntentOnly: false,
    applicability: 'jurisdiction_concrete',
    catalogPaused: false,
    // Всё, что считается, делает менеджер: цитата лишь клиента «да» не доказывает
    // (все 13 «да» ред. 4 на Stimma цитируют и реплику менеджера, #2853).
    evidenceSpeaker: 'agent',
    texts: {
      uk: {
        name: 'Порушив правила персональних даних',
        question: 'Чи порушив менеджер правила роботи з персональними даними клієнта?',
        guidance:
          'Юрисдикція: {jurisdiction}. Рахується: менеджер просить повний номер картки, CVV, PIN, пароль або код з SMS; після ознаки, що відповів не клієнт (каже, що нічого не замовляв, що номер чи замовлення не його, що не знає цієї людини), усе одно називає дані клієнта — повне ім’я, адресу чи відділення, телефон, — не з’ясувавши, з ким говорить (навіть якщо потім виявиться, що замовив родич); диктує дані клієнта сторонній людині. Не рахується: дзвінок щодо замовлення тому, хто визнає замовлення своїм чи для себе, — звернення на ім’я, зачитування замовлення, суми, міста чи відділення; опис чи фото товару; репліки явно з іншої розмови; питання про контакти для замовлення або доставки; клієнт сам називає свої дані; останні цифри картки для звірки.',
      },
      ru: {
        name: 'Нарушил правила персональных данных',
        question: 'Нарушил ли менеджер правила работы с персональными данными клиента?',
        guidance:
          'Юрисдикция: {jurisdiction}. Считается: менеджер просит полный номер карты, CVV, PIN, пароль или код из SMS; после признака, что ответил не клиент (говорит, что ничего не заказывал, что номер или заказ не его, что не знает этого человека), всё равно называет данные клиента — полное имя, адрес или отделение, телефон, — не выяснив, с кем говорит (даже если потом окажется, что заказал родственник); диктует данные клиента постороннему. Не считается: звонок по заказу тому, кто признаёт заказ своим или для себя, — обращение по имени, зачитывание заказа, суммы, города или отделения; описание или фото товара; реплики явно из другого разговора; вопрос о контактах для заказа или доставки; клиент сам называет свои данные; последние цифры карты для сверки.',
      },
      en: {
        name: 'Broke personal data rules',
        question:
          'Did the employee break the rules for handling the customer’s personal data?',
        guidance:
          'Jurisdiction: {jurisdiction}. Counts: the employee asks for the full card number, CVV, PIN, password or SMS code; after a sign that whoever answered is not the customer (ordered nothing, not their number or order, does not know this person), still names the customer’s data — full name, address or pickup point, phone — before finding out who it is (even if a relative ordered); reads the customer’s data to a third party. Does not count: an order call to whoever accepts the order as theirs or for them — greeting by name, reading back the order, amount, city or pickup point; a product description or photo; lines clearly from another call; asking for order or delivery contact details; the customer giving their own data; the last card digits to verify.',
      },
      es: {
        name: 'Violó las reglas de datos personales',
        question:
          '¿El empleado violó las reglas de manejo de los datos personales del cliente?',
        guidance:
          'Jurisdicción: {jurisdiction}. Cuenta: el empleado pide el número completo de tarjeta, CVV, PIN, contraseña o código SMS; tras una señal de que quien contestó no es el cliente (no pidió nada, número o pedido ajeno, no conoce a esa persona), aun así dice datos del cliente —nombre completo, dirección o punto de entrega, teléfono— sin averiguar con quién habla (aunque pidiera un familiar); dicta datos del cliente a un tercero. No cuenta: llamar por el pedido a quien lo acepta como suyo o para sí (saludo por nombre, lectura del pedido, importe, ciudad o punto de entrega); descripción o foto del producto; frases claramente de otra llamada; pedir contactos del pedido o la entrega; el cliente da sus propios datos; últimos dígitos de tarjeta para verificar.',
      },
    },
  },
];

/**
 * Принимает ли встроенный с этим ключом список слов клиента (#2784). Одно
 * правило на маршрут правки и экран критерия.
 */
export function builtinTakesWordList(key: string | null | undefined): boolean {
  return BUILTIN_CRITERIA_CATALOG.some(
    (entry) => entry.key === key && entry.applicability === 'word_list'
  );
}

/**
 * Отказ пробы по списку слов (#2826): те же коды, что у сохранения. Список
 * бывает только у флажка встроенного со списком; встроенному со списком без
 * слов оценивать нечего — проба без этого сожгла бы слот потолка организации
 * на выборку, где все записи пропущены. Общая для edge-api (до платного вызова)
 * и workflows-api (внутренний контракт): правило в одном месте.
 */
export function trialWordListRefusal(input: {
  builtinKey: string | undefined;
  resultType: string;
  wordList: readonly string[] | undefined;
}): 'word_list_not_allowed' | 'builtin_word_list_empty' | null {
  const hasWords = (input.wordList?.length ?? 0) > 0;
  const takesWordList =
    input.builtinKey !== undefined &&
    input.resultType === 'flag' &&
    builtinTakesWordList(input.builtinKey);
  if (hasWords && !takesWordList) return 'word_list_not_allowed';
  if (takesWordList && !hasWords) return 'builtin_word_list_empty';
  return null;
}

/** Чья реплика доказывает ответ встроенного с этим ключом; `undefined` — любая. */
export function builtinEvidenceSpeaker(
  key: string | null | undefined
): 'agent' | undefined {
  return BUILTIN_CRITERIA_CATALOG.find((entry) => entry.key === key)?.evidenceSpeaker;
}

export function builtinCatalogEntry(key: BuiltinCriterionKey): BuiltinCriterionEntry {
  const entry = BUILTIN_CRITERIA_CATALOG.find((e) => e.key === key);
  if (!entry) throw new Error(`builtin criterion not in catalog: ${key}`);
  return entry;
}

/**
 * Готовый текст встроенного на языке организации. Юрисдикция обязательна
 * ровно там, где текст её ждёт: у `jurisdiction_concrete` без подписи
 * юрисдикции подставить нечего — это ошибка вызывающего, а не пустое место в
 * вопросе.
 */
export function renderBuiltinCriterion(
  key: BuiltinCriterionKey,
  language: Language,
  params: { jurisdictionLabel?: string } = {}
): BuiltinCriterionTexts {
  const entry = builtinCatalogEntry(key);
  const texts = entry.texts[language];
  const needs = Object.values(texts).some((t) =>
    t.includes(BUILTIN_JURISDICTION_PLACEHOLDER)
  );
  if (!needs) return { ...texts };
  const label = params.jurisdictionLabel?.trim();
  if (!label) throw new Error(`builtin criterion ${key} needs a jurisdiction label`);
  const fill = (t: string) => t.split(BUILTIN_JURISDICTION_PLACEHOLDER).join(label);
  return {
    name: fill(texts.name),
    question: fill(texts.question),
    guidance: fill(texts.guidance),
  };
}
